import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@apple/utils/pagination/models';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { productQueryOptions } from '@apple/features/product/queries/management';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/keywords')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
		});

		return {
			getProductKeywordsQueryOptions: productQueryOptions.keywordListing,
		};
	},
	loader: ({ context: { queryClient, getProductKeywordsQueryOptions } }) => {
		void queryClient.prefetchQuery(getProductKeywordsQueryOptions);
	},
	component: KeywordManagementRoute,
});

function KeywordManagementRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const { getProductKeywordsQueryOptions } = Route.useRouteContext();
	const productsQuery = useQuery(getProductKeywordsQueryOptions);

	const table = useTable({
		features,
		data: productsQuery.data || [],
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: false,
		columnResizeMode: 'onChange',
		initialState: {
			pagination: {
				pageSize: DEFAULT_PAGE_SIZE,
				pageIndex: DEFAULT_PAGE_INDEX,
			},
		},
		columns: [
			{
				accessorKey: 'description',
				header: t('keywords.labels.description'),
				filter: {
					group: t('keywords.filterGroups.details'),
				},
				size: 250,
				cell: ({ row }) => (
					<Link
						to='/manage/keywords/$keywordId'
						params={{ keywordId: row.original.keywordId.toString() }}
						title={row.original.description}
						children={row.original.description}
					/>
				),
			},
			{
				accessorKey: 'updatedDate',
				header: t('keywords.labels.updatedDate'),
				enableColumnFilter: false,
				size: 250,
				cell: ({ row }) => <FormattedDate value={row.original.updatedDate} humanize />,
			},
			{
				accessorKey: 'productCount',
				header: t('keywords.labels.productCount'),
				enableColumnFilter: false,
				size: 250,
			},
		],
	});

	return (
		<TableLayout
			title={t('keywords.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('keywords.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/keywords/add',
							state: { from: location },
						})
					}
					data-testid='add-tag'
				/>,
			]}
		>
			<DataTable table={table} loading={productsQuery.isFetching} />
		</TableLayout>
	);
}
